import {copy_object, merge_deep, present} from '@bitstillery/common/lib/utils'
import {unchecked} from '@bitstillery/common/lib/unchecked'
import {$t, api, events, notifier, store} from '@bitstillery/common/app'
import {logger} from '@bitstillery/mithril'

import type {GetActivePromotionResponse, SalesOrderDetailsResponse} from '@bitstillery/common/api/fact2server'
import type {ApiResult} from '@bitstillery/common/lib/api'

import {temporary, type StatePortal} from '../lib/state'

import {quantity_registry} from '@/lib/quantity_registry'
import {$m, $s} from '@/app'

export const OrderStatus = {
    CANCELLED: 'Cancelled',
    CONFIRMED: 'Confirmed',
    FINALIZED: 'Finalized',
    INVOICED: 'Invoiced',
    NEW: 'New',
    PENDING: 'Pending',
    SAVED: ['Pending', 'Finalized'],
}

/**
 * The promotion as the last call left it. Its gifts are the promotion's untyped JSON, which the portal
 * reads as its own gift shape; the "no active promotion" OkResponse and a failed call's error body carry
 * none of its fields. A response without gifts still carries the key, undefined, and `merge_deep` copies
 * that over the store's previous gifts.
 */
type ActivePromotion = Partial<
    Omit<GetActivePromotionResponse, 'gifts'> & {gifts: StatePortal['promotion']['gifts'] | null | undefined}
>

let active_promotion: ActivePromotion | null = null

/**
 * Load the promotion state from the backend and update
 * the local store with it.
 * @returns
 */
export async function get_active_promotion() {
    if (globalThis.__SSR_MODE__) {
        return
    }

    $s.promotion.loading = true
    logger.debug('retrieving active promotions', {module: 'order'})
    const {result, success} = await api.get('portal/promotions/active')
    if (!success) {
        active_promotion = {}
        $s.promotion.active = false
        return
    }
    active_promotion = 'artkey' in result ? {...result, gifts: unchecked<ActivePromotion['gifts']>(result.gifts)} : {}

    // One promotion is active? Enable the promotions UI elements.
    // We'll later distinguish between different promotions.
    const now = new Date()
    const end_date = new Date(active_promotion.end_date ?? NaN)
    $s.promotion.active = end_date > now
    const grace_date = new Date(active_promotion.grace_date ?? NaN)
    // This means either in the active period OR in the grace period.
    $s.promotion.active_grace = grace_date > now
    merge_deep($s.promotion, active_promotion, {
        current: active_promotion.gifts?.[0]?.id,
        loading: false,
        // Number() coerces null and undefined the way `+` would: null as 0, undefined as NaN.
        turnover_draft: Number(active_promotion.turnover_draft) + $m.cart.cart_amount(),
        turnover_redeemable: active_promotion.turnover_redeemable,
        unlocked: true, // Only used on single-order promotions
    })

    $m.order.active_promotion_status()
}

export async function load_active_voucher(order_artkey: number) {
    const {result, success} = await api.get(`portal/orders/${order_artkey}/vouchers`)
    if (success) {
        $s.cart.vouchers.$available.value = result
    }
}

// Whether the portal has been left since the last reload. Both signals below set it, so switching
// away as a tab and switching away as a window each cost exactly one reload on the way back.
let left_the_portal = false
let reloading_current_order = false

/**
 * Reload the order the cart points at, and say so when a different one turns out to be current.
 *
 * Both ways into this end the same: the order was confirmed somewhere else, current-order hands
 * back its successor and the basket empties. Unexplained, that reads as lost work — the customer's
 * own doing, but not from here.
 */
export async function reload_current_order() {
    // load_current_order() validates the order it loads, and a validation is one of the callers.
    if (reloading_current_order) {
        return
    }
    reloading_current_order = true
    const previous_artkey = $s.cart.artkey
    try {
        await load_current_order()
    } finally {
        reloading_current_order = false
    }

    // A failed load leaves no order behind; only a different order is one confirmed elsewhere.
    if (previous_artkey && $s.cart.artkey && $s.cart.artkey !== previous_artkey) {
        notifier.notify(
            $t('notifications.order_confirmed_elsewhere', {new_order: `S${$s.cart.artkey}`, order: `S${previous_artkey}`}),
            'warning',
            10_000,
        )
    }
}

/**
 * Reload the order when the visitor comes back to a portal they had left.
 *
 * A customer works the portal in two tabs, or two windows, or on a phone next to a desktop; what
 * the other one added, removed, booked or spent a voucher on is invisible here until something asks
 * the backend. Taking the whole order is what keeps cart lines, booked items, additionals and
 * vouchers in step at once.
 *
 * Visibility catches another tab, focus catches another window; neither sees the other's case.
 */
export function reload_order_on_return() {
    if (globalThis.__SSR_MODE__) {
        return
    }

    const returned = async () => {
        // A change of the customer's own is still on its way out; the flag stays set, so the next
        // return picks the sync up instead of this one rolling the change back.
        if ($m.cart.has_pending_cart_writes()) {
            return
        }
        if (!left_the_portal || !$s.identity.token || !$s.cart.artkey || $s.cart.loading) {
            return
        }
        left_the_portal = false
        logger.info('back in the portal; reloading the order', {module: 'order'})
        await reload_current_order()
    }

    window.addEventListener('blur', () => {
        left_the_portal = true
        $m.cart.flush_cart_writes()
    })
    window.addEventListener('focus', () => void returned())
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            left_the_portal = true
            $m.cart.flush_cart_writes()
        } else {
            void returned()
        }
    })
}

/**
 * Loads the active order for the current user.
 *
 * This function first retrieves any active promotions and then fetches
 * the current active order from the user's portal. If no active order is
 * found, it notifies the user of the failure. Otherwise, it proceeds to
 * load the details of the active order.
 *
 * @returns A promise that resolves when the active order has been loaded.
 */
export async function load_current_order() {
    if (globalThis.__SSR_MODE__) {
        return
    }
    void get_active_promotion()
    // A 204 answers without a body, so even a success can leave no order behind it.
    const {result: active_order, success}: ApiResult<SalesOrderDetailsResponse | undefined> =
        await api.get('portal/user/current-order')
    if (!success || !active_order?.artkey) {
        // It is important to notify the customer when loading the cart fails;
        // Otherwise he/she makes a futile effort to fill the cart without persistency.
        notifier.notify($t('notifications.order_failure'), 'danger', 7000)
        // Clear the spinner; there is no order to load so the cart must not hang.
        $s.cart.loading = false
        return
    }

    // Clear the quantity registry and reload the order (especially cart.value state)
    // In case the bottle/case preference changes.
    quantity_registry.clear()

    logger.info(`loading active order: ${active_order.artkey}`, {module: 'order'})
    Object.assign($s.cart, {artkey: null, loading: true, reference: null})
    try {
        const full_order = await load_full_order(active_order)
        // load_full_order returns {} when the order or any of its sub-requests
        // failed to load. Bail gracefully instead of dereferencing sales_order
        // below (which would throw and leave the cart stuck on the spinner).
        if (!full_order.sales_order) {
            notifier.notify($t('notifications.order_failure'), 'danger', 7000)
            return
        }

        Object.assign($s.cart, {
            amount: {},
            errors: copy_object(temporary.cart.errors),
            includes_excise: full_order.sales_order.includes_excise,
            items: {},
            requested_delivery_date: full_order.sales_order.requested_delivery_date,
            sales_order_additionals: full_order.sales_order_additionals,
            sales_order_credit_items: full_order.sales_order_credit_items,
            sales_order_items: full_order.sales_order_items,
            sales_order_tbo_items: full_order.sales_order_tbo_items,
            unavailable: [],
        })

        for (const portal_order_item of full_order.portal_order_items) {
            if (!(portal_order_item.case_artkey in $s.cart.items)) {
                // A loaded line has its cases but no `value` yet; add_cart_item_state derives it from them.
                $m.cart.add_cart_item_state({...portal_order_item, value: 0}, true)
            }
        }

        const voucher = $m.cart.voucher_additional()
        const voucher_code = voucher?.description
        $s.cart.vouchers.active = Boolean(voucher_code)

        void store.save()
        active_promotion_status()
        await $m.cart.update_cart_offer_items(true)
        // #1927 - Temporarily disable COO. This code will self-destruct in 6 months.
        void (await $m.offer.load_product_photos(
            Object.values($s.cart.items).flatMap((cart_item) =>
                cart_item.offer_item_artkey ? [cart_item.offer_item_artkey] : [],
            ),
        ))

        Object.assign($s.cart, {
            artkey: full_order.sales_order.artkey,
            loading: false,
            reference: full_order.sales_order.reference,
        })
        // No need to await this
        void $m.cart.validate_cart()
        await load_active_voucher(full_order.sales_order.artkey)
        return full_order
    } finally {
        // Guarantee the loading spinner always clears, even when a step above
        // throws or returns early; otherwise the cart hangs on the spinner.
        $s.cart.loading = false
    }
}

/**
 * Loads a complete order using either an order object or an order key.
 *
 * This asynchronous function retrieves various components of a sales
 * order and aggregates them into a single comprehensive order object.
 * The components fetched include sales order items, additional items,
 * portal order items, tbo items, credit items, and invoice details.
 *
 * The function sorts both sales order items and portal order items alphabetically
 * by the product name before returning the final composed order object.
 *
 * @param artkey_or_order - The order's key or an order object.
 * @returns A promise that resolves to an object containing
 *                            all components of the loaded order.
 */
export async function load_full_order(artkey_or_order: number | SalesOrderDetailsResponse) {
    let sales_order
    if (typeof artkey_or_order === 'number') {
        const sales_order_response = await api.get(`portal/orders/${artkey_or_order}`)
        if (!sales_order_response.success) {
            return {}
        }
        sales_order = sales_order_response.result
    } else {
        sales_order = artkey_or_order
    }

    const [
        sales_order_items_response,
        additionals_response,
        portal_order_items_response,
        tbo_items_response,
        credit_items_response,
        invoice_details_response,
    ] = await Promise.all([
        api.get(`portal/orders/${sales_order.artkey}/sales-order-items`),
        api.get(`portal/orders/${sales_order.artkey}/additionals`),
        api.get(`portal/orders/${sales_order.artkey}/portal-order-items`),
        api.get(`portal/orders/${sales_order.artkey}/tbo-items`),
        api.get(`portal/orders/${sales_order.artkey}/credit-items`),
        api.get(`portal/orders/${sales_order.artkey}/invoice-details`),
    ])
    if (
        !sales_order_items_response.success ||
        !additionals_response.success ||
        !portal_order_items_response.success ||
        !tbo_items_response.success ||
        !credit_items_response.success ||
        !invoice_details_response.success
    ) {
        return {}
    }
    const sales_order_additionals = additionals_response.result
    const sales_order_tbo_items = tbo_items_response.result
    const sales_order_credit_items = credit_items_response.result
    const invoice_details = invoice_details_response.result

    const portal_order_items = portal_order_items_response.result.toSorted((a, b) => {
        const _a = a.product_name.toUpperCase()
        const _b = b.product_name.toUpperCase()
        if (_a < _b) {
            return -1
        }
        if (_a > _b) {
            return 1
        }
        return 0
    })

    const sales_order_items = sales_order_items_response.result.toSorted((a, b) => {
        const _a = a.product_name.toUpperCase()
        const _b = b.product_name.toUpperCase()
        if (_a < _b) {
            return -1
        }
        if (_a > _b) {
            return 1
        }
        return 0
    })

    return {
        invoice_details,
        portal_order_items,
        sales_order,
        sales_order_additionals,
        sales_order_credit_items,
        sales_order_items,
        sales_order_tbo_items,
    }
}

/**
 * This function is separated from get_active_promotion, because
 * it calculates with local cart state, and is updated whenever
 * the cart changed(the API call is debounced). The `draft` and `redeemable`
 * values are separate numbers, while preview combines both when needed
 * for the promotion calculation.
 * @returns
 */
export function active_promotion_status() {
    if (!$s.promotion.active_grace) {
        return
    }

    const {token_value} = $s.promotion
    const {turnover_redeemable} = $s.promotion
    const turnover_draft = Math.max(
        Number(present(active_promotion, 'active promotion').turnover_draft) + $m.cart.cart_amount(),
        0,
    )

    const tokens_redeemable = Math.floor(turnover_redeemable / token_value)
    const tokens_draft = Math.floor(turnover_draft / token_value)
    const promotion_type = $s.promotion.type

    if (promotion_type === 'single-order') {
        // Single-order specific logic
    } else if (promotion_type === 'multi-order') {
        const tokens_selected = Object.entries($s.promotion.lookup)
            .map(([slug, amount]) => {
                const gift = $s.promotion.gifts.find((g) => g.id === slug)
                if (gift) {
                    return gift.tokens_required * amount
                }
                return 0
            })
            .reduce((total, tokens) => total + tokens, 0)

        // Reset our selection, if we have more than available tokens selected.
        if (tokens_selected > $s.promotion.tokens_redeemable) {
            // Remove the local selection and update the store
            for (const key of Object.keys($s.promotion.lookup)) {
                delete $s.promotion.lookup[key]
            }
            void store.save()
        }

        $s.promotion.tokens_selected = tokens_selected
    }

    merge_deep($s.promotion, {
        tokens_draft,
        tokens_redeemable,
        turnover_draft: turnover_draft,
    })
}

export function init() {
    events.on('identity.login', () => {
        // Establish (or create) the current order so $s.cart.artkey is set right
        // after an in-place dialog login; otherwise the first cart add POSTs to
        // orders/null/* and the v2 backend rejects it. load_current_order() also
        // refreshes the active promotion, so no separate get_active_promotion call.
        void load_current_order()
    })
}
